// What the reader does on the board, decided without a board: a move is
// refused while the lock forbids it or when it is not legal, a pawn reaching
// the last rank becomes a queen (or, in 'ask' mode, waits for the reader's
// pick), and anything else goes to the review, which steps the game or
// starts a line. The board puts the piece back on any refusal.
import type { Move, PieceType, Square } from '@chess/api-model';
import type { Review } from './index.ts';
import type { BoardInput, BoardLock, BoardOutcome, BoardView, PendingPromotion } from './board.ts';

/** What a pawn reaching the last rank becomes without a piece named: a queen, or the reader's pick. */
export type PromotionMode = 'queen' | 'ask';

/** The board's own passing state: a picked square, a promotion waiting for its piece. */
export interface BoardUi {
  selected?: Square;
  pendingPromotion?: PendingPromotion;
}

export interface BoardControllerDeps {
  review: Pick<Review, 'play'>;
  /** The board as it is now; its lock decides whether a move is taken. */
  view: () => Pick<BoardView, 'lock'>;
  /** The legal moves of the position on the board. */
  legal: () => readonly Move[];
  /** Default 'queen', as the board has always done. */
  promotion?: PromotionMode;
}

const PROMOTION_ORDER: readonly PieceType[] = ['q', 'r', 'b', 'n'];

function playable(lock: BoardLock): boolean {
  return lock === 'free' || lock === 'judged';
}

export class BoardController {
  private readonly deps: BoardControllerDeps;
  private current: BoardUi = {};

  constructor(deps: BoardControllerDeps) {
    this.deps = deps;
  }

  /** The picked square and the pending promotion. */
  ui(): BoardUi {
    return this.current;
  }

  /** Forget the pick and any pending promotion, e.g. when the position changes. */
  reset(): void {
    if (this.current.selected !== undefined || this.current.pendingPromotion) this.current = {};
  }

  input(input: BoardInput): BoardOutcome {
    switch (input.kind) {
      case 'select':
        this.current = { ...this.current, selected: input.square };
        if (input.square === undefined) delete this.current.selected;
        return { kind: 'selected' };
      case 'promote': {
        const pending = this.current.pendingPromotion;
        this.current = {};
        if (!pending || !input.piece) return { kind: 'cancelled' };
        return this.move(pending.from, pending.to, input.piece);
      }
      case 'move':
        this.current = {};
        return this.move(input.from, input.to, input.promotion);
    }
  }

  private move(from: Square, to: Square, promotion: PieceType | undefined): BoardOutcome {
    const lock = this.deps.view().lock;
    if (!playable(lock)) return { kind: 'refused', reason: lock };
    const candidates = this.deps.legal().filter((m) => m.from === from && m.to === to);
    if (candidates.length === 0) return { kind: 'refused', reason: 'illegal' };
    const promotes = candidates.some((m) => m.flags.promotion);
    let piece = promotes ? promotion : undefined;
    if (promotes && !piece) {
      if (this.deps.promotion === 'ask') {
        const offered = new Set(candidates.map((m) => m.promotion).filter((p): p is PieceType => !!p));
        this.current = { pendingPromotion: { from, to, choices: PROMOTION_ORDER.filter((p) => offered.has(p)) } };
        return { kind: 'awaitingPromotion' };
      }
      piece = 'q';
    }
    const move = candidates.find((m) => (m.promotion ?? undefined) === piece);
    if (!move) return { kind: 'refused', reason: 'illegal' };
    const ok = this.deps.review.play(piece ? { from, to, promotion: piece } : { from, to });
    if (ok) return { kind: 'played', move };
    // The review refused it: whatever forbids it now, or the rules.
    const now = this.deps.view().lock;
    return { kind: 'refused', reason: playable(now) ? 'illegal' : now };
  }
}
